import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Settings,
  FileText,
  ArrowLeft,
  Save,
  Check,
  Layers
} from 'lucide-react';
import Layout from '../components/Layout';
import { GrapesJsEditor, HtmlCss } from '../components/GrapesJsEditor';
import { invoiceComponents } from '../components/GjsCustomComponents';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../hooks/useI18n';
import { templateAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import { TemplateType, InvoiceTemplate } from '../types';
import '../components/grapesJs.css';
import { useCompany } from "../context/CompanyContext";

const TemplateManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, isRTL } = useI18n();
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);
  const [staticOptions, setStaticOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'custom' | 'standard'>('standard');
  const { selectedCompany, loadCompanies } = useCompany();

  const [htmlCss, setHtmlCss] = useState<HtmlCss | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }
    };
  }, [previewPdfUrl]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [templatesData, staticData] = await Promise.all([
        templateAPI.getTemplates(),
        templateAPI.getStaticTemplateOptions()
      ]);
      setTemplates(templatesData);
      setStaticOptions(staticData);

      // If there's a static template already selected, default to standard tab
      const hasStatic = templatesData.some(t => t.type === TemplateType.STATIC);
      if (!hasStatic && templatesData.length > 0) {
        setActiveTab('custom');
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setTemplateName('');
    setShowBuilder(true);
  };

  const handleEditTemplate = (template: InvoiceTemplate) => {
    setSelectedTemplate(template);
    setTemplateName(template.name || '');
    setShowBuilder(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm(t('templates.confirmDelete'))) {
      return;
    }

    try {
      await templateAPI.deleteTemplate(templateId);
      await loadData();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      setError(getErrorMessage(err));
    }
  };

  const handleSaveTemplate = async (htmlCss: HtmlCss) => {
    try {
      if (!templateName.trim()) {
        setError(t('templates.nameRequired'));
        return;
      }

      const templateData = {
        name: templateName,
        inlinedHtmlAndCss: htmlCss.inlinedInnerHtmlAndCss || '',
        type: TemplateType.WYSIWYG
      };

      if (selectedTemplate?.id) {
        await templateAPI.updateTemplate(selectedTemplate.id, templateData);
      } else {
        await templateAPI.createTemplate(templateData);
      }
      setShowBuilder(false);
      await loadData();
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError(getErrorMessage(err));
    }
  };

  const handleSelectStaticTemplate = async (name: string) => {

    try {
      setLoading(true);
      await templateAPI.upsertStaticTemplate(name, selectedCompany?.selectedCompanyId);
      // Refresh company data to update selected template ID
      await loadCompanies();
      await loadData();
    } catch (err: any) {
      console.error('Error selecting static template:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  //TODO idali check
  const handleSelectCustomTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      await templateAPI.selectTemplate(templateId, selectedCompany?.selectedCompanyId);
      window.location.reload();
    } catch (err: any) {
      console.error('Error selecting custom template:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = async (template: InvoiceTemplate) => {
    try {
      setPreviewLoading(true);
      setShowPreview(true);

      const blob = await templateAPI.previewPDFBytes(template.id);
      const pdfUrl = URL.createObjectURL(blob);
      setPreviewPdfUrl(pdfUrl);
    } catch (err: any) {
      console.error('Error generating PDF preview:', err);
      setError(getErrorMessage(err));
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const customTemplates = templates.filter(t => t.type === TemplateType.WYSIWYG);
  const selectedStaticTemplate = templates.find(t => t.type === TemplateType.STATIC);

  if (showBuilder) {
    return (<Layout>
      <div style={{ height: '100vh', overflow: 'hidden' }}>
        <div style={{
          zIndex: 1000,
          background: '#fff',
          borderBottom: '1px solid #ddd',
          padding: '10px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => setShowBuilder(false)}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors border border-gray-300`}
            >
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <span>{t('templates.backToTemplates')}</span>
            </button>
            <h2 style={{ margin: 0, fontSize: '18px' }}>
              {selectedTemplate ? t('templates.editTemplate') : t('templates.createTemplate')}
            </h2>
            <div className={`flex items-center ${isRTL ? 'mr-4' : 'ml-4'}`}>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('templates.templateNamePlaceholder')}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ width: '300px' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => handleSaveTemplate(htmlCss!)}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} px-4 py-2 rounded-md transition-colors ${htmlCss
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
              <Save size={16} />
              <span>{t('templates.saveTemplate')}</span>
            </button>
          </div>
        </div>
        <div className="gjs-container">
          <GrapesJsEditor
            locale="en"
            setHtmlCss={setHtmlCss}
            customComponents={invoiceComponents}
            predefinedInlineHtmlCss={selectedTemplate?.inlinedHtmlAndCss}
            subscriberId={user?.entityId}
            templateId={selectedTemplate?.id}
          />
        </div>
      </div>
    </Layout>);
  }

  if (showPreview) {
    return (<Layout>
      <div style={{ height: '100vh', overflow: 'hidden' }}>
        <div style={{
          zIndex: 1000,
          background: '#fff',
          borderBottom: '1px solid #ddd',
          padding: '10px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => {
                setShowPreview(false);
                if (previewPdfUrl) {
                  URL.revokeObjectURL(previewPdfUrl);
                  setPreviewPdfUrl('');
                }
              }}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors border border-gray-300`}
            >
              <ArrowLeft size={16} />
              <span>{t('templates.backToTemplates')}</span>
            </button>
            <h2 style={{ margin: 0, fontSize: '18px' }}>{t('templates.templatePdfPreview')}</h2>
          </div>
        </div>
        <div style={{ marginTop: '60px', height: 'calc(100vh - 60px)', overflow: 'auto' }}>
          {previewLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className='ml-3 text-gray-600'>{t('templates.generatingPdfPreview')}</span>
            </div>
          ) : previewPdfUrl ? (
            <iframe
              src={previewPdfUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Template PDF Preview"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <span className="text-gray-600">No preview available</span>
            </div>
          )}
        </div>
      </div>
    </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-4'}`}>
            <button
              onClick={() => navigate('/settings')}
              className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} text-gray-600 hover:text-gray-900 transition-colors`}
            >
              <ArrowLeft size={20} className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <span>{t('templates.backToSettings')}</span>
            </button>
          </div>
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
            <Settings className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">{t('templates.title')}</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab('standard')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative ${activeTab === 'standard' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="flex items-center gap-2">
              <Layers size={18} />
              {t('templates.standardTemplates')}
            </div>
            {activeTab === 'standard' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative ${activeTab === 'custom' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="flex items-center gap-2">
              <Edit size={18} />
              {t('templates.customTemplates')}
            </div>
            {activeTab === 'custom' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {activeTab === 'standard' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {staticOptions.map((option) => {
              const isSelected = selectedCompany?.selectedCompany?.templateId === templates.find(t => t.type === TemplateType.STATIC && t.name === option)?.id;
              return (
                <div
                  key={option}
                  className={`relative bg-white rounded-xl overflow-hidden border-2 transition-all cursor-pointer hover:shadow-lg ${isSelected ? 'border-blue-600 ring-4 ring-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  onClick={() => handleSelectStaticTemplate(option)}
                >
                  <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center relative group">
                    <FileText className={`w-20 h-20 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all" />
                    {isSelected && (
                      <div className="absolute top-4 right-4 bg-blue-600 text-white p-1.5 rounded-full shadow-lg">
                        <Check size={20} />
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-white border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <h3 className={`font-bold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                        {option.replace(/-/g, ' ').toUpperCase()}
                      </h3>
                      {isSelected && <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{t('common.selected')}</span>}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{t('templates.standardTemplateDesc')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {/* Create Template Button */}
            <div className="mb-6">
              <button
                onClick={handleCreateTemplate}
                className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors`}
              >
                <Plus className="w-4 h-4" />
                <span>{t('templates.createNewTemplate')}</span>
              </button>
            </div>

            {/* Templates List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : customTemplates.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('templates.noTemplatesFound')}</h3>
                <p className="text-gray-500 mb-6">
                  {t('templates.createFirstTemplate')}
                </p>
                <button
                  onClick={handleCreateTemplate}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors shadow-md"
                >
                  {t('templates.createTemplate')}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {customTemplates.map((template) => {
                  const isSelected = selectedCompany?.selectedCompany?.templateId === template.id;
                  return (
                    <div
                      key={template.id}
                      className={`relative bg-white rounded-xl overflow-hidden border-2 transition-all cursor-pointer hover:shadow-lg ${isSelected ? 'border-blue-600 ring-4 ring-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                      onClick={() => handleSelectCustomTemplate(template.id)}
                    >
                      <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center relative group">
                        <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} flex items-center gap-2`} onClick={(e) => e.stopPropagation()}>
                          <button
                              onClick={() => handlePreviewTemplate(template)}
                              className="p-2 bg-white/90 backdrop-blur-sm text-blue-600 hover:bg-blue-50 rounded-full shadow-sm transition-colors"
                              title={t('templates.previewPdf')}
                              disabled={previewLoading}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                              onClick={() => handleEditTemplate(template)}
                              className="p-2 bg-white/90 backdrop-blur-sm text-orange-600 hover:bg-orange-50 rounded-full shadow-sm transition-colors"
                              title={t('templates.edit')}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="p-2 bg-white/90 backdrop-blur-sm text-red-600 hover:bg-red-50 rounded-full shadow-sm transition-colors"
                              title={t('templates.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <FileText className={`w-20 h-20 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all" />
                        {isSelected && (
                          <div className="absolute top-4 right-4 bg-blue-600 text-white p-1.5 rounded-full shadow-lg">
                            <Check size={20} />
                          </div>
                        )}
                      </div>
                      <div className="p-4 bg-white border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <h3 className={`font-bold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                            {template.name || `${t('templates.template')} ${template.id.slice(0, 8)}`}
                          </h3>
                          {isSelected && <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{t('common.selected')}</span>}
                        </div>
                        <div className="mt-4">

                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default TemplateManagementPage;
